import { Button } from '@talentmesh/ui/components/button';
import { Input } from '@talentmesh/ui/components/input';
import { Label } from '@talentmesh/ui/components/label';
import { cn } from '@talentmesh/ui/lib/utils';
import { Loader2 } from 'lucide-react';
import { FormProvider } from 'react-hook-form';
import type { AccountCreationFormValues } from './AccountCreationForm.schema';
import { useAccountCreationForm } from './useAccountCreationForm';
import SubmissionError from '@/Pages/AcceptInvitation/Components/SumbissionError';
import UIStrings from '@/Utils/UIStrings';

export type { AccountCreationFormValues } from './AccountCreationForm.schema';

export interface AccountCreationFormAPI {
    isLoading: boolean;
    isSubmitting: boolean;
    allowAccountSetup: boolean;
    submitHandler: (values: AccountCreationFormValues) => Promise<void>;
}

interface AccountCreationFormProps {
    initialCompanyName: string;
    initialFirstName: string;
    initialLastName: string;
    isSubmitting: boolean;
    submitHandler: (values: AccountCreationFormValues) => Promise<void>;
    isError?: boolean;
}

const AccountCreationForm = ({
    initialCompanyName,
    initialFirstName,
    initialLastName,
    submitHandler,
    isSubmitting,
    isError,
}: AccountCreationFormProps) => {
    const form = useAccountCreationForm({
        initialFirstName,
        initialLastName,
        initialCompanyName,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        <FormProvider {...form}>
            <form className="flex flex-col gap-6" onSubmit={handleSubmit(submitHandler)}>
                {isError && <SubmissionError />}
                <p className="text-muted-foreground text-md leading-relaxed">{UIStrings.AddYourDetails}</p>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-6">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="firstName">{UIStrings.FirstNameWithAesterisk}</Label>
                        <Input
                            id="firstName"
                            type="text"
                            autoComplete="given-name"
                            aria-invalid={Boolean(errors.firstName)}
                            className={cn(errors.firstName && 'border-destructive')}
                            {...register('firstName')}
                        />
                        {errors.firstName?.message && (
                            <p className="text-destructive text-sm" role="alert">
                                {errors.firstName.message}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="lastName">{UIStrings.LastNameWithAesterisk}</Label>
                        <Input
                            id="lastName"
                            type="text"
                            autoComplete="family-name"
                            aria-invalid={Boolean(errors.lastName)}
                            className={cn(errors.lastName && 'border-destructive')}
                            {...register('lastName')}
                        />
                        {errors.lastName?.message && (
                            <p className="text-destructive text-sm" role="alert">
                                {errors.lastName.message}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="companyName">{`${UIStrings.CompanyName}*`}</Label>
                    <Input
                        id="companyName"
                        type="text"
                        autoComplete="organization"
                        disabled={initialCompanyName !== ''}
                        aria-invalid={Boolean(errors.companyName)}
                        className={cn(errors.companyName && 'border-destructive')}
                        {...register('companyName')}
                    />
                    {errors.companyName?.message && (
                        <p className="text-destructive text-sm" role="alert">
                            {errors.companyName.message}
                        </p>
                    )}
                </div>
                <Button type="submit" size="lg" className="mt-2 w-full tracking-wide" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 size-4 shrink-0 animate-spin" aria-hidden />}
                    {UIStrings.SaveAndContinue}
                </Button>
            </form>
        </FormProvider>
    );
};

export default AccountCreationForm;
